/**
 * RTCDataChannel 背压等待策略：低水位事件负责正常唤醒，定期采样仅用于识别“持续无进展”。
 * 总等待时间不设固定上限，避免把低带宽但仍在发送的连接误判为 stalled。
 */

/** 缓冲量采样间隔；仅在超过高水位、发送泵暂停期间启用。 */
const DRAIN_PROGRESS_POLL_MS = 1_000;
/** 缓冲量连续不下降达到此时长才判定 stalled。 */
const DRAIN_STALL_TIMEOUT_MS = 30_000;

/**
 * 等待 DataChannel 缓冲量降至低水位。
 *
 * 低速链路只要缓冲量仍在下降就不会超时；close/error 或连续无进展会拒绝 Promise。
 */
export const waitForDataChannelDrain = (
	channel: RTCDataChannel,
): Promise<void> =>
	new Promise((resolve, reject) => {
		if (channel.readyState !== "open") {
			reject(new Error("File channel is not open"));
			return;
		}

		let settled = false;
		let lastBufferedAmount = channel.bufferedAmount;
		let lastProgressAt = Date.now();
		let progressTimer: ReturnType<typeof setTimeout> | undefined;

		const cleanup = () => {
			channel.removeEventListener("bufferedamountlow", onDrain);
			channel.removeEventListener("close", onClosed);
			channel.removeEventListener("error", onClosed);
			if (progressTimer !== undefined) clearTimeout(progressTimer);
		};
		const settle = (error?: Error) => {
			if (settled) return;
			settled = true;
			cleanup();
			if (error) reject(error);
			else resolve();
		};
		const onDrain = () => settle();
		const onClosed = () => settle(new Error("File channel closed"));
		const checkProgress = () => {
			if (channel.readyState !== "open") {
				settle(new Error("File channel closed"));
				return;
			}

			const currentBufferedAmount = channel.bufferedAmount;
			if (currentBufferedAmount <= channel.bufferedAmountLowThreshold) {
				settle();
				return;
			}
			if (currentBufferedAmount < lastBufferedAmount) {
				lastProgressAt = Date.now();
			}
			lastBufferedAmount = currentBufferedAmount;

			if (Date.now() - lastProgressAt >= DRAIN_STALL_TIMEOUT_MS) {
				settle(new Error("File channel drain stalled"));
				return;
			}
			progressTimer = setTimeout(checkProgress, DRAIN_PROGRESS_POLL_MS);
		};
		progressTimer = setTimeout(checkProgress, DRAIN_PROGRESS_POLL_MS);

		channel.addEventListener("bufferedamountlow", onDrain);
		channel.addEventListener("close", onClosed);
		channel.addEventListener("error", onClosed);
		// 注册监听前缓冲区可能已跨过低水位；注册后复查，避免错过一次性事件。
		if (channel.bufferedAmount <= channel.bufferedAmountLowThreshold) settle();
	});
